import React from 'react';
import PropTypes from 'prop-types';
import { DialogContent, Paper } from '@material-ui/core';
import {
  Avatar,
  Icon,
  ListItem,
  TextField,
  Typography,
} from '@knockrentals/knock-shared-web-v2';
import { useTransferProperty } from './use-transfer-property';
import {
  FeatureDialogActions,
  makeCss,
} from '../../../../../knock-design-system';
import { propertyDetailsPropTypes } from '../../prop-types';
import {
  DEFAULT_MANAGER_OWNER_USERNAME,
  LEASING_TEAM_PUBLIC_ID,
  PROPERTY_DETAILS_FIELDS,
  TRANSFER_LABELS,
  TRANSFER_PRIMARY_BUTTON_TEXT,
} from './constants';

const getPropertyAddress = (propertyDetails) => {
  return {
    city: propertyDetails[PROPERTY_DETAILS_FIELDS.CITY],
    state: propertyDetails[PROPERTY_DETAILS_FIELDS.STATE],
    street: propertyDetails[PROPERTY_DETAILS_FIELDS.STREET],
    zipCode: propertyDetails[PROPERTY_DETAILS_FIELDS.ZIP],
  };
};

const useCss = makeCss({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  paper: {
    overflow: 'hidden',
  },
});

const TransferProperty = ({
  closeDialog,
  propertyDetails,
  transferProperty,
}) => {
  const css = useCss();

  const {
    handleTransferOnClick,
    isTransferButtonDisabled,
    transferDetails,
    updateTransferDetails,
    updateValidationErrors,
    validationErrors,
  } = useTransferProperty({
    transferProperty,
  });

  const propertyAddress = getPropertyAddress(propertyDetails);
  const { city, state, street, zipCode } = propertyAddress || {};

  const handleOnChange = (value, event) => {
    const { name } = event.target;
    updateTransferDetails({ [name]: value });
  };

  const handleOnBlur = (e) => {
    const { name, value } = e.target;
    updateTransferDetails({ [name]: value.trim() });
  };

  const handleOnFocus = (e) => {
    const { name } = e.target;
    if (validationErrors[name]) {
      updateValidationErrors({ [name]: '' });
    }
  };

  return (
    <React.Fragment>
      <DialogContent css={css.dialogContent}>
        <Paper css={css.paper} variant="outlined">
          <ListItem
            endSlot={
              <div>
                <Typography>{street}</Typography>
                <Typography>{`${city}, ${state} ${zipCode}`}</Typography>
              </div>
            }
            component="div"
            primary="Transfer Property"
            secondary={propertyDetails.propertyName}
            startSlot={
              <Avatar color="slate.100" size={40}>
                <Icon name="building-modern-outlined" color="slate.600" />
              </Avatar>
            }
          ></ListItem>
        </Paper>

        <Typography variant="subtitle2">Transfer Team Info</Typography>

        <TextField
          error={Boolean(validationErrors[LEASING_TEAM_PUBLIC_ID])}
          fullWidth
          helperText={validationErrors[LEASING_TEAM_PUBLIC_ID]}
          id={LEASING_TEAM_PUBLIC_ID}
          label={TRANSFER_LABELS[LEASING_TEAM_PUBLIC_ID]}
          name={LEASING_TEAM_PUBLIC_ID}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          placeholder={`Enter ${TRANSFER_LABELS[LEASING_TEAM_PUBLIC_ID]}`}
          variant="outlined"
          value={transferDetails[LEASING_TEAM_PUBLIC_ID] || ''}
        />

        <TextField
          error={Boolean(validationErrors[DEFAULT_MANAGER_OWNER_USERNAME])}
          fullWidth
          helperText={validationErrors[DEFAULT_MANAGER_OWNER_USERNAME]}
          id={DEFAULT_MANAGER_OWNER_USERNAME}
          label={TRANSFER_LABELS[DEFAULT_MANAGER_OWNER_USERNAME]}
          name={DEFAULT_MANAGER_OWNER_USERNAME}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          placeholder={`Enter ${TRANSFER_LABELS[DEFAULT_MANAGER_OWNER_USERNAME]}`}
          value={transferDetails[DEFAULT_MANAGER_OWNER_USERNAME] || ''}
          variant="outlined"
        />
      </DialogContent>

      <FeatureDialogActions
        isPrimaryButtonDisabled={isTransferButtonDisabled}
        primaryButtonText={TRANSFER_PRIMARY_BUTTON_TEXT}
        primaryButtonOnClick={handleTransferOnClick}
        tertiaryButtonOnClick={closeDialog}
      />
    </React.Fragment>
  );
};

TransferProperty.propTypes = {
  closeDialog: PropTypes.func,
  propertyDetails: propertyDetailsPropTypes,
  transferProperty: PropTypes.func,
};

export default TransferProperty;
